import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Wallet, Banknote, Clock, CheckCircle2, Search, Settings, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const PAYOUT_LABELS = {
  in_attesa: "In attesa", approvato: "Approvato", in_elaborazione: "In elaborazione",
  pagato: "Pagato", annullato: "Annullato",
};
const PAYOUT_COLORS = {
  in_attesa: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  approvato: "text-cyan-300 border-cyan-500/30 bg-cyan-500/10",
  in_elaborazione: "text-indigo-300 border-indigo-500/30 bg-indigo-500/10",
  pagato: "text-[#00E676] border-[#00E676]/30 bg-[#00E676]/10",
  annullato: "text-red-300 border-red-500/30 bg-red-500/10",
};
const NEXT = {
  in_attesa: [["approve", "Approva"], ["cancel", "Annulla"]],
  approvato: [["process", "Elabora"], ["cancel", "Annulla"]],
  in_elaborazione: [["mark_paid", "Segna pagato"], ["cancel", "Annulla"]],
};

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl p-5 border ${accent ? "border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/10 to-[#131316]" : "border-white/10 bg-[#1C1C1F]"}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400"><Icon className={`h-4 w-4 ${accent ? "text-[#00E676]" : "text-zinc-400"}`} /> {label}</div>
      <div className={`text-2xl font-display font-bold mt-2 ${accent ? "text-[#00E676]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Liquidazioni() {
  const [sum, setSum] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [pstatus, setPstatus] = useState("");
  const [adj, setAdj] = useState(null);

  const load = () => {
    api.get("/admin/payouts/summary").then(({ data }) => setSum(data)).catch(() => {});
    api.get(`/admin/wallets${type ? `?type=${type}` : ""}`).then(({ data }) => setWallets(data || [])).catch(() => {});
    api.get(`/admin/payouts${pstatus ? `?status=${pstatus}` : ""}`).then(({ data }) => setPayouts(data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, pstatus]);

  const liquidate = async (w) => {
    try { await api.post("/admin/payouts", { owner_id: w.owner_id, owner_type: w.owner_type }); toast.success(`Liquidazione creata per ${w.owner_name}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const transition = async (p, action) => {
    try { await api.post(`/admin/payouts/${p.id}/transition`, { action }); toast.success("Stato liquidazione aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitAdj = async () => {
    try {
      await api.post("/admin/wallet/adjust", { owner_id: adj.owner_id, owner_type: adj.owner_type, kind: adj.kind, amount: parseFloat(adj.amount), description: adj.description });
      toast.success("Rettifica registrata"); setAdj(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filteredWallets = q ? wallets.filter((w) => (w.owner_name || "").toLowerCase().includes(q.toLowerCase())) : wallets;

  return (
    <div className="space-y-6" data-testid="payouts-dashboard">
      {sum && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat testid="sum-rider" label="Da pagare ai rider" value={`€${sum.rider_available.toFixed(2)}`} icon={Banknote} accent />
          <Stat testid="sum-business" label="Da liquidare alle attività" value={`€${sum.business_available.toFixed(2)}`} icon={Wallet} accent />
          <Stat testid="sum-open" label="Liquidazioni aperte" value={`€${sum.payouts_open.toFixed(2)}`} icon={Clock} />
          <Stat testid="sum-paid" label="Pagamenti effettuati" value={`€${sum.payouts_paid.toFixed(2)}`} icon={CheckCircle2} />
        </div>
      )}
      {sum && sum.missing_payout_method > 0 && (
        <div data-testid="missing-iban-banner" className="rounded-xl border border-amber-400/30 bg-amber-400/10 text-amber-200 text-sm px-4 py-2.5 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> {sum.missing_payout_method} destinatari con saldo disponibile non hanno ancora inserito l'IBAN: non potranno essere pagati finché non lo aggiungono.
        </div>
      )}

      {adj && (
        <div data-testid="adjust-form" className="rounded-2xl p-5 border border-[#00E676]/30 bg-[#131316] space-y-3">
          <div className="flex items-center justify-between"><h3 className="text-sm font-semibold">Rettifica / Rimborso · {adj.owner_name}</h3><button onClick={() => setAdj(null)} className="text-zinc-400 text-sm">Chiudi</button></div>
          <div className="grid sm:grid-cols-4 gap-3">
            <div><Label>Tipo</Label>
              <Select value={adj.kind} onValueChange={(v) => setAdj({ ...adj, kind: v })}>
                <SelectTrigger data-testid="adjust-kind" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="bonus">Bonus</SelectItem><SelectItem value="incentive">Incentivo</SelectItem><SelectItem value="refund">Rimborso</SelectItem><SelectItem value="penalty">Penalità</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Importo (€)</Label><Input data-testid="adjust-amount" type="number" step="0.5" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            <div className="sm:col-span-2"><Label>Descrizione</Label><Input data-testid="adjust-desc" value={adj.description} onChange={(e) => setAdj({ ...adj, description: e.target.value })} placeholder="Motivo…" className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          </div>
          <div className="text-[11px] text-zinc-500">Bonus / Incentivo / Rimborso accreditano il saldo disponibile. La Penalità lo riduce.</div>
          <Button size="sm" data-testid="adjust-submit" disabled={!(parseFloat(adj.amount) > 0)} onClick={submitAdj} className="bg-[#00E676] text-black rounded-md">Registra rettifica</Button>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500">Saldi da liquidare</h3>
          <div className="flex items-center gap-2">
            <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-zinc-500" /><Input data-testid="wallet-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca destinatario" className="bg-[#0A0A0C] border-white/10 h-9 pl-8 w-52" /></div>
            <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="wallet-type-filter" className="w-36 bg-[#0A0A0C] border-white/10 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="rider">Rider</SelectItem><SelectItem value="business">Attività</SelectItem><SelectItem value="professional">Professionisti</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-white/10">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10 text-zinc-500 text-left"><th className="p-3">Destinatario</th><th className="p-3">Tipo</th><th className="p-3">Disponibile</th><th className="p-3">In attesa</th><th className="p-3">In liquidazione</th><th className="p-3">IBAN</th><th className="p-3">Azione</th></tr></thead>
            <tbody>
              {filteredWallets.map((w) => (
                <tr key={w.owner_id} data-testid={`wallet-row-${w.owner_id}`} className="border-b border-white/5">
                  <td className="p-3 font-medium">{w.owner_name || w.owner_id.slice(-6)}</td>
                  <td className="p-3 text-zinc-400">{w.owner_type === "rider" ? "Rider" : "Attività"}</td>
                  <td className="p-3 text-[#00E676] font-semibold">€{Number(w.available || 0).toFixed(2)}</td>
                  <td className="p-3 text-amber-300">€{Number(w.pending || 0).toFixed(2)}</td>
                  <td className="p-3 text-cyan-300">€{Number(w.in_payout || 0).toFixed(2)}</td>
                  <td className="p-3 text-xs" data-testid={`wallet-iban-${w.owner_id}`}>{w.has_payout_method ? (<span className="text-zinc-300"><span className="font-mono">{w.payout_iban_masked}</span><br /><span className="text-zinc-500">{w.payout_holder}</span></span>) : (<span className="text-amber-300/90 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Manca IBAN</span>)}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button size="sm" data-testid={`liquidate-${w.owner_id}`} disabled={!(w.available > 0)} onClick={() => liquidate(w)} className="bg-[#00E676] text-black rounded-md disabled:opacity-30"><Send className="h-3.5 w-3.5 mr-1" /> Liquida</Button>
                      <Button size="sm" variant="secondary" data-testid={`adjust-${w.owner_id}`} onClick={() => setAdj({ owner_id: w.owner_id, owner_type: w.owner_type, owner_name: w.owner_name, kind: "bonus", amount: "", description: "" })}>Rettifica</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredWallets.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-500">Nessun saldo da liquidare.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500">Storico liquidazioni</h3>
          <Select value={pstatus || "all"} onValueChange={(v) => setPstatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="payout-status-filter" className="w-44 bg-[#0A0A0C] border-white/10 h-9"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{Object.keys(PAYOUT_LABELS).map((s) => <SelectItem key={s} value={s}>{PAYOUT_LABELS[s]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          {payouts.map((p) => (
            <div key={p.id} data-testid={`payout-${p.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-semibold">€{Number(p.amount).toFixed(2)} <span className="text-zinc-500 font-normal">· {p.owner_name || p.owner_id.slice(-6)} ({p.owner_type})</span></div>
                <div className="text-xs text-zinc-500">{String(p.created_at).slice(0, 16).replace("T", " ")} · via {p.provider}{p.provider_ref ? ` · rif ${p.provider_ref}` : ""}{p.payout_method?.iban ? ` · IBAN ${p.payout_method.iban.slice(0, 4)}…${p.payout_method.iban.slice(-4)}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[11px] rounded-full px-2 py-0.5 border ${PAYOUT_COLORS[p.status]}`}>{PAYOUT_LABELS[p.status]}</span>
                {(NEXT[p.status] || []).map(([action, label]) => (
                  <Button key={action} size="sm" variant={action === "cancel" ? "destructive" : "secondary"} data-testid={`payout-${action}-${p.id}`} onClick={() => transition(p, action)}>{label}</Button>
                ))}
              </div>
            </div>
          ))}
          {payouts.length === 0 && <div className="text-zinc-500 py-12 text-center border border-dashed border-white/10 rounded-xl">Nessuna liquidazione.</div>}
        </div>
      </div>
    </div>
  );
}

function ConfigForm() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/wallet-config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const save = async (section, body) => {
    try { await api.put(`/admin/wallet-config/${section}`, body); toast.success("Configurazione salvata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!cfg) return <div className="text-zinc-500 py-16 text-center">Caricamento configurazione…</div>;
  const rc = cfg.rider_comp, co = cfg.commission, po = cfg.payout;
  const setRc = (k, v) => setCfg({ ...cfg, rider_comp: { ...rc, [k]: v } });
  const setCo = (k, v) => setCfg({ ...cfg, commission: { ...co, [k]: v } });
  const setPo = (k, v) => setCfg({ ...cfg, payout: { ...po, [k]: v } });

  return (
    <div className="space-y-6 max-w-3xl" data-testid="wallet-config">
      <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
        <h4 className="font-semibold mb-3 flex items-center gap-2"><Wallet className="h-4 w-4 text-[#00E676]" /> Compenso rider</h4>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Tariffa fissa (€)</Label><Input type="number" step="0.1" data-testid="cfg-base-fee" value={rc.base_fee} onChange={(e) => setRc("base_fee", parseFloat(e.target.value) || 0)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          <div><Label>€ / km</Label><Input type="number" step="0.05" data-testid="cfg-per-km" value={rc.per_km} onChange={(e) => setRc("per_km", parseFloat(e.target.value) || 0)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        </div>
        <div className="mt-4">
          <Label>Fasce orarie bonus (JSON)</Label>
          <textarea data-testid="cfg-time-bands" defaultValue={JSON.stringify(rc.time_bands || [], null, 0)} onBlur={(e) => { try { setRc("time_bands", JSON.parse(e.target.value)); } catch { toast.error("JSON fasce orarie non valido"); } }} className="w-full mt-1 bg-[#0A0A0C] border border-white/10 rounded-md p-2 text-xs font-mono h-16" />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div><Label>Bonus meteo (€)</Label><Input type="number" step="0.5" data-testid="cfg-weather" value={rc.weather_bonus?.bonus || 0} onChange={(e) => setRc("weather_bonus", { ...(rc.weather_bonus || {}), enabled: true, bonus: parseFloat(e.target.value) || 0 })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          <div><Label>Bonus festività (€)</Label><Input type="number" step="0.5" data-testid="cfg-holiday" value={rc.holiday_bonus?.bonus || 0} onChange={(e) => setRc("holiday_bonus", { ...(rc.holiday_bonus || {}), enabled: true, bonus: parseFloat(e.target.value) || 0 })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        </div>
        <Button size="sm" data-testid="save-rider-comp" onClick={() => save("rider-comp", rc)} className="bg-[#00E676] text-black rounded-md mt-4">Salva compenso rider</Button>
      </div>

      <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
        <h4 className="font-semibold mb-3">Commissione piattaforma</h4>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Commissione default (%)</Label><Input type="number" step="1" data-testid="cfg-commission" value={Math.round((co.default_rate || 0) * 100)} onChange={(e) => setCo("default_rate", (parseFloat(e.target.value) || 0) / 100)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        </div>
        <div className="mt-4"><Label>Override per categoria (JSON, es. {"{"}"pizzeria":0.10{"}"})</Label><textarea data-testid="cfg-commission-cat" defaultValue={JSON.stringify(co.by_category || {}, null, 0)} onBlur={(e) => { try { setCo("by_category", JSON.parse(e.target.value)); } catch { toast.error("JSON categorie non valido"); } }} className="w-full mt-1 bg-[#0A0A0C] border border-white/10 rounded-md p-2 text-xs font-mono h-14" /></div>
        <Button size="sm" data-testid="save-commission" onClick={() => save("commission", co)} className="bg-[#00E676] text-black rounded-md mt-4">Salva commissioni</Button>
      </div>

      <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
        <h4 className="font-semibold mb-3">Liquidazioni</h4>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Giorni di clearing</Label><Input type="number" data-testid="cfg-clearing" value={po.clearing_days} onChange={(e) => setPo("clearing_days", parseInt(e.target.value) || 0)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          <div><Label>Provider payout</Label>
            <Select value={po.provider} onValueChange={(v) => setPo("provider", v)}>
              <SelectTrigger data-testid="cfg-provider" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{(cfg.providers || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <label className="flex items-center justify-between mt-4 bg-[#0A0A0C] border border-white/10 rounded-lg p-3 cursor-pointer">
          <span className="text-sm">Pagamento in <b>contanti</b> abilitato (mostrato ai clienti al checkout)</span>
          <input type="checkbox" data-testid="cfg-cash-enabled" checked={!!po.cash_enabled} onChange={(e) => setPo("cash_enabled", e.target.checked)} className="h-5 w-5 accent-[#00E676]" />
        </label>
        <Button size="sm" data-testid="save-payout" onClick={() => save("payout", po)} className="bg-[#00E676] text-black rounded-md mt-4">Salva liquidazioni</Button>
      </div>
    </div>
  );
}

function CashPanel() {
  const [report, setReport] = useState(null);
  const [rows, setRows] = useState([]);
  const load = () => {
    api.get("/admin/cash/report").then(({ data }) => setReport(data)).catch(() => {});
    api.get("/admin/cash/to-remit").then(({ data }) => setRows(data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const reconcile = async (r) => {
    try { await api.post("/admin/cash/reconcile", { rider_id: r.owner_id }); toast.success(`Versamento confermato per ${r.owner_name || "rider"}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6" data-testid="cash-dashboard">
      {report && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat testid="cash-online" label="Incassato online" value={`€${Number(report.online_collected).toFixed(2)}`} icon={Wallet} />
          <Stat testid="cash-collected" label="Incassato contanti" value={`€${Number(report.cash_collected).toFixed(2)}`} icon={Banknote} accent />
          <Stat testid="cash-to-remit" label="Contanti da versare" value={`€${Number(report.cash_to_remit).toFixed(2)}`} icon={Clock} />
          <Stat testid="cash-reconciled" label="Contanti riconciliati" value={`€${Number(report.cash_reconciled).toFixed(2)}`} icon={CheckCircle2} />
        </div>
      )}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Rider con contante da versare</h3>
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.owner_id} data-testid={`cash-owed-${r.owner_id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4 flex items-center justify-between">
              <div><div className="font-semibold">{r.owner_name || r.owner_id.slice(-6)}</div><div className="text-xs text-zinc-500">Rider</div></div>
              <div className="flex items-center gap-3"><span className="text-amber-300 font-semibold">€{Number(r.cash_owed).toFixed(2)}</span><Button size="sm" data-testid={`reconcile-${r.owner_id}`} onClick={() => reconcile(r)} className="bg-[#00E676] text-black rounded-md">Conferma versamento</Button></div>
            </div>
          ))}
          {rows.length === 0 && <div className="text-zinc-500 py-12 text-center border border-dashed border-white/10 rounded-xl">Nessun contante da versare.</div>}
        </div>
      </div>
    </div>
  );
}

export default function Payouts() {
  const [tab, setTab] = useState("liquidazioni");
  return (
    <div>
      <div className="flex gap-2 mb-5">
        <button data-testid="tab-liquidazioni" onClick={() => setTab("liquidazioni")} className={`text-sm px-4 py-2 rounded-lg border flex items-center gap-2 ${tab === "liquidazioni" ? "border-[#00E676] text-[#00E676] bg-[#00E676]/5" : "border-white/10 text-zinc-400"}`}><Banknote className="h-4 w-4" /> Liquidazioni</button>
        <button data-testid="tab-contanti" onClick={() => setTab("contanti")} className={`text-sm px-4 py-2 rounded-lg border flex items-center gap-2 ${tab === "contanti" ? "border-[#00E676] text-[#00E676] bg-[#00E676]/5" : "border-white/10 text-zinc-400"}`}><Banknote className="h-4 w-4" /> Contanti</button>
        <button data-testid="tab-config" onClick={() => setTab("config")} className={`text-sm px-4 py-2 rounded-lg border flex items-center gap-2 ${tab === "config" ? "border-[#00E676] text-[#00E676] bg-[#00E676]/5" : "border-white/10 text-zinc-400"}`}><Settings className="h-4 w-4" /> Configurazione</button>
      </div>
      {tab === "liquidazioni" ? <Liquidazioni /> : tab === "contanti" ? <CashPanel /> : <ConfigForm />}
    </div>
  );
}
